import React, { useState, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';
import { submitApplication, fileToBase64 } from '../../../services/jobApplicationService';

const DAY_TICKETS_BANNER_CDN =
  'https://BelvoirCastlePull.b-cdn.net/Website/1.webp';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 3rem 2rem 5rem;
  animation: ${fadeUp} 0.5s ease;

  @media (max-width: 768px) {
    padding: 2rem 1.25rem 4rem;
  }
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  font-size: 0.95rem;
  text-decoration: none;
  margin-bottom: 2.5rem;
  transition: gap 0.2s ease;

  &:hover { gap: 0.7rem; }
`;

const JobHeader = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2.5rem 3rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 2rem;
  border-top: 4px solid ${props => props.theme.colors.accent};

  @media (max-width: 768px) {
    padding: 1.75rem 1.5rem;
  }
`;

const JobTitle = styled.h1`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.4rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const JobSubtitle = styled.p`
  color: ${props => props.theme.colors.accent};
  font-weight: 700;
  font-size: 1.05rem;
  margin: 0 0 1.5rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem 2rem;
  margin-bottom: 1.75rem;
`;

const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 160px;
`;

const MetaLabel = styled.span`
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.primary};
  opacity: 0.55;
`;

const MetaValue = styled.span`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  line-height: 1.4;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 0.35rem 0.9rem;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  background: ${props => props.highlight
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.07)'};
  color: ${props => props.highlight ? 'white' : props.theme.colors.primary};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: 2rem;
  align-items: start;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem 2.5rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1.25rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.5rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(16,29,64,0.08);
`;

const BodyText = styled.p`
  font-size: 1.05rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1rem;

  &:last-child { margin-bottom: 0; }
`;

const SubHeading = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.15rem;
  color: ${props => props.theme.colors.primary};
  margin: 1.5rem 0 0.6rem;
`;

const BulletList = styled.ul`
  font-size: 1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 0.75rem;
  padding-left: 1.4rem;

  li { margin-bottom: 0.35rem; }
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin: 1rem 0;
`;

const TechCard = styled.div`
  background: ${props => props.theme.colors.offWhite};
  border: 2px solid rgba(16,29,64,0.08);
  border-radius: 12px;
  padding: 1rem 1.25rem;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
  }
`;

const TechLabel = styled.div`
  font-weight: 700;
  color: ${props => props.theme.colors.primary};
  font-size: 0.9rem;
  margin-bottom: 0.35rem;
`;

const TechDesc = styled.div`
  font-size: 0.82rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.75;
  line-height: 1.5;
`;

const StickyCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06);
  border-top: 4px solid ${props => props.theme.colors.primary};
  position: sticky;
  top: 140px;
`;

const CardTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.3rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 0 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  font-size: 0.92rem;
  margin-bottom: 0.4rem;

  span.req { color: ${props => props.theme.colors.accent}; }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder { color: #aaa; }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.7rem 0.9rem;
  border: 2px solid rgba(16,29,64,0.12);
  border-radius: 8px;
  font-size: 0.95rem;
  font-family: inherit;
  resize: vertical;
  min-height: 100px;
  transition: border-color 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }

  &::placeholder { color: #aaa; }
`;

const FileUploadArea = styled.label`
  display: block;
  border: 2px dashed ${props => props.hasFile
    ? props.theme.colors.primary
    : 'rgba(16,29,64,0.2)'};
  border-radius: 10px;
  padding: 1rem 1.25rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.hasFile
    ? 'rgba(16,29,64,0.04)'
    : 'transparent'};

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    background: rgba(16,29,64,0.03);
  }

  input { display: none; }
`;

const FileUploadText = styled.div`
  font-size: 0.88rem;
  color: ${props => props.theme.colors.primary};
  font-weight: ${props => props.selected ? '600' : '500'};
  opacity: ${props => props.selected ? 1 : 0.6};
`;

const FileUploadHint = styled.div`
  font-size: 0.82rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.5;
  margin-top: 0.35rem;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid rgba(16,29,64,0.08);
  margin: 1.25rem 0;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 1rem;
  background: ${props => props.theme.colors.primary};
  color: white;
  border: 2px solid ${props => props.theme.colors.primary};
  border-radius: 10px;
  font-size: 1rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.25s ease;
  font-family: ${props => props.theme.fonts.main};
  letter-spacing: 0.02em;

  &:hover:not(:disabled) {
    background: ${props => props.theme.colors.accent};
    border-color: ${props => props.theme.colors.accent};
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16,29,64,0.2);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div`
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  background: ${props => props.type === 'success'
    ? '#d4edda'
    : props.type === 'error'
    ? '#f8d7da'
    : '#d1ecf1'};
  color: ${props => props.type === 'success'
    ? '#155724'
    : props.type === 'error'
    ? '#721c24'
    : '#0c5460'};
  border: 1px solid ${props => props.type === 'success'
    ? '#c3e6cb'
    : props.type === 'error'
    ? '#f5c6cb'
    : '#bee5eb'};
`;

const SmallNote = styled.p`
  font-size: 0.8rem;
  color: ${props => props.theme.colors.primary};
  opacity: 0.6;
  line-height: 1.5;
  margin: 0.5rem 0 0;
  text-align: center;
`;

const SoftwareDigitalDevelopment = () => {
  const formRef = useRef(null);

  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    portfolio: '',
    coverNote: '',
  });
  const [cvFile, setCvFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = e => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleFileChange = (setter) => (e) => {
    const file = e.target.files[0] || null;
    setter(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);

    if (!cvFile) {
      setStatus({ type: 'error', message: 'Please attach your CV.' });
      return;
    }
    if (!coverFile) {
      setStatus({ type: 'error', message: 'Please attach your cover letter.' });
      return;
    }

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];
    if (!allowedTypes.includes(cvFile.type)) {
      setStatus({ type: 'error', message: 'CV must be a PDF, DOC, or DOCX file.' });
      return;
    }
    if (!allowedTypes.includes(coverFile.type)) {
      setStatus({ type: 'error', message: 'Cover letter must be a PDF, DOC, or DOCX file.' });
      return;
    }

    if (!form.coverNote.trim()) {
      setStatus({ type: 'error', message: 'Please complete the cover note — tell us about yourself.' });
      return;
    }

    setSubmitting(true);
    try {
      const [cvData, coverLetterData] = await Promise.all([
        fileToBase64(cvFile),
        fileToBase64(coverFile),
      ]);

      await submitApplication({
        roleId: 'software-digital-graduate',
        roleTitle: 'Software & Digital Development Assistant — Graduate Role',
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        portfolio: form.portfolio,
        coverNote: form.coverNote,
        cvData,
        cvFileName: cvFile.name,
        coverLetterData,
        coverLetterFileName: coverFile.name,
      });

      setSubmitted(true);
      setStatus({ type: 'success', message: 'Application submitted! We\'ll be in touch.' });
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Something went wrong. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <ParallaxBanner
        title="Graduate: Software & Digital Development Assistant"
        subtitle="Belvoir Castle — Historic Estate · Digital Innovation · Technology Stack"
        image={DAY_TICKETS_BANNER_CDN}
      />

      <ContentContainer>
        <BackLink to="/about/careers">← Back to Careers</BackLink>

        <JobHeader>
          <JobSubtitle>Belvoir Castle, Leicestershire</JobSubtitle>
          <JobTitle>Software & Digital Development Assistant</JobTitle>

          <MetaRow>
            <MetaItem>
              <MetaLabel>Location</MetaLabel>
              <MetaValue>Belvoir Castle, Leicestershire</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Type</MetaLabel>
              <MetaValue>Graduate role</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Hours</MetaLabel>
              <MetaValue>24–40 hrs/week (flexible)</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Open to</MetaLabel>
              <MetaValue>Recent graduates</MetaValue>
            </MetaItem>
            <MetaItem>
              <MetaLabel>Salary</MetaLabel>
              <MetaValue>Competitive (paid role)</MetaValue>
            </MetaItem>
          </MetaRow>

          <TagRow>
            <Tag highlight>Graduate Role</Tag>
            <Tag highlight>Paid Position</Tag>
            <Tag>Software Development</Tag>
            <Tag>React / Node.js / WordPress</Tag>
            <Tag>AI & Automation</Tag>
            <Tag>Permanent Role Opportunity</Tag>
          </TagRow>
        </JobHeader>

        <Grid>
          <div>
            <Section>
              <SectionTitle>About Belvoir Castle</SectionTitle>
              <BodyText>
                Belvoir Castle is the historic stately home of the Duke and Duchess of Rutland, set within a stunning
                Leicestershire estate between Grantham and Melton Mowbray. Our vision is to share the history and
                beauty of the Estate through delivering exceptional experiences — for our local community, visitors
                from across the country, and guests from around the world.
              </BodyText>
              <BodyText>
                We are committed to preserving, growing, and developing the Estate in a sustainable way — honouring
                our past whilst boldly embracing the future. And that future, increasingly, is digital.
              </BodyText>
            </Section>

            <Section>
              <SectionTitle>About This Opportunity</SectionTitle>
              <BodyText>
                This is a genuine paid graduate role with real responsibility from day one. We are looking for
                someone who is ambitious, technically curious, and keen to understand what sits behind the
                technology — not just how digital platforms look, but how they work, connect, perform, and
                improve customer experience.
              </BodyText>
              <BodyText>
                The opportunity combines WordPress, React, Node.js, AI tools, analytics, automation, digital
                marketing systems, content platforms, customer journeys, booking flows, and wider digital
                innovation within the heritage, hospitality, events, and visitor experience sectors.
              </BodyText>
              <BodyText>
                The successful candidate will join the team from day one with mentoring from experienced
                marketing, digital, and development professionals, exposure to Belvoir Castle's full technology
                stack, and the opportunity to work with Her Grace, The Duchess of Rutland — gaining insight into
                the vision, brand, storytelling, and commercial development of the Estate.
              </BodyText>
            </Section>

            <Section>
              <SectionTitle>Who This Is For</SectionTitle>
              <BodyText>
                This role is open to <strong>recent graduates</strong> ready to launch their career in software
                and digital development.
              </BodyText>
              <BulletList>
                <li>Graduates in Computer Science, Software Development, Digital Media, Marketing Technology, or related fields</li>
                <li>Self-taught candidates with strong digital or technical experience and demonstrable projects</li>
                <li>Graduates looking for a paid route into real-world development with a genuine pathway to a permanent role</li>
              </BulletList>
              <BodyText>
                We are especially interested in graduates who strive for high standards, enjoy solving problems,
                want to keep learning, and are excited by the opportunity to help a historic estate become a leader
                in digital innovation.
              </BodyText>
            </Section>

            <Section>
              <SectionTitle>Our Technology Stack</SectionTitle>
              <BodyText>
                Belvoir Castle runs a modern, full-service digital operation. Here is what powers it — and what
                you will work with and contribute to:
              </BodyText>
              <TechGrid>
                <TechCard>
                  <TechLabel>Web & CMS</TechLabel>
                  <TechDesc>WordPress — content management, custom mu-plugins, REST API extensions, SEO optimisation</TechDesc>
                </TechCard>
                <TechCard>
                  <TechLabel>Frontend</TechLabel>
                  <TechDesc>React — dynamic web interfaces, interactive booking flows, component-based architecture</TechDesc>
                </TechCard>
                <TechCard>
                  <TechLabel>Mobile App</TechLabel>
                  <TechDesc>React Native — cross-platform iOS and Android app for guest experience and estate operations</TechDesc>
                </TechCard>
                <TechCard>
                  <TechLabel>Backend & APIs</TechLabel>
                  <TechDesc>Node.js & PHP — server-side logic, REST APIs, booking systems, payment processing, automation</TechDesc>
                </TechCard>
                <TechCard>
                  <TechLabel>AI & Automation</TechLabel>
                  <TechDesc>AI-powered tools, personalisation, customer insight, automated workflows, and operational efficiency</TechDesc>
                </TechCard>
                <TechCard>
                  <TechLabel>Analytics & Data</TechLabel>
                  <TechDesc>Performance tracking, conversion analytics, booking data, and customer journey measurement</TechDesc>
                </TechCard>
              </TechGrid>
            </Section>

            <Section>
              <SectionTitle>Responsibilities</SectionTitle>

              <SubHeading>Software & Development</SubHeading>
              <BulletList>
                <li>Contribute to the WordPress codebase — custom plugins, REST API integrations, theme development, and CMS improvements</li>
                <li>Work on the React frontend — building and improving UI components, booking flows, and customer-facing features</li>
                <li>Support backend development in Node.js and PHP — API endpoints, data processing, and third-party integrations</li>
                <li>Contribute to the React Native mobile app for guest experience and estate operations</li>
                <li>Help implement and maintain automated systems, email workflows, and digital operational tools</li>
              </BulletList>

              <SubHeading>Digital Innovation & AI</SubHeading>
              <BulletList>
                <li>Research, test, and integrate AI tools into marketing, customer experience, and operational workflows</li>
                <li>Help identify and build automations that reduce manual effort and improve conversion</li>
                <li>Contribute to personalisation features and data-driven customer journey improvements</li>
              </BulletList>

              <SubHeading>Analytics & Performance</SubHeading>
              <BulletList>
                <li>Monitor and report on digital performance — traffic, conversion, booking data, and customer engagement</li>
                <li>Work with the team to turn data into actionable insight and improvement</li>
                <li>Support SEO implementation and technical site health across platforms</li>
              </BulletList>

              <SubHeading>Collaboration & Learning</SubHeading>
              <BulletList>
                <li>Work closely with the Marketing Manager, Digital Manager, and senior development professionals</li>
                <li>Gain exposure to every part of the technology stack from day one</li>
                <li>Bring ideas, ask questions, and contribute to a culture of continuous improvement</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>What We Are Looking For</SectionTitle>

              <SubHeading>Essential</SubHeading>
              <BulletList>
                <li>Recent graduate in Computer Science, Software Development, Digital Media, Marketing Technology, or equivalent demonstrable experience</li>
                <li>Genuine technical curiosity — a desire to understand how things work, not just how they look</li>
                <li>Some hands-on coding experience: HTML, CSS, JavaScript, PHP, Python, or similar — formal or self-taught</li>
                <li>Ambitious and driven with a high personal standard for quality and accuracy</li>
                <li>Strong communication skills and the ability to work as part of a small, fast-moving team</li>
                <li>Keen to learn continuously and take ownership of real work from day one</li>
              </BulletList>

              <SubHeading>Highly Desirable</SubHeading>
              <BulletList>
                <li>Familiarity with React, Node.js, or WordPress development</li>
                <li>Experience with REST APIs, JSON, or backend integrations</li>
                <li>Interest in or experience with AI tools (ChatGPT, Claude, automation platforms)</li>
                <li>Any experience with mobile development (React Native, Swift, or Kotlin)</li>
                <li>Understanding of SEO, analytics, or digital marketing systems</li>
                <li>Personal projects, GitHub portfolio, or freelance work that demonstrates initiative</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>What You Will Get</SectionTitle>
              <BulletList>
                <li>A paid graduate role with a competitive salary</li>
                <li>Real responsibility and real projects from day one — not a coffee-making internship</li>
                <li>Mentoring from experienced marketing, digital, and development professionals</li>
                <li>Exposure to the full Belvoir Castle technology stack — and the opportunity to genuinely contribute to it</li>
                <li>The opportunity to work alongside Her Grace, The Duchess of Rutland, gaining insight into the vision, brand, and commercial development of the Estate</li>
                <li>A working environment unlike any other — a historic, living castle estate in the English countryside</li>
                <li>A genuine pathway to a permanent full-time role for the right candidate</li>
              </BulletList>
            </Section>

            <Section>
              <SectionTitle>Digital Areas You Will Support</SectionTitle>
              <BulletList>
                <li>Belvoir Castle website and booking platform</li>
                <li>Guest-facing mobile application</li>
                <li>Email marketing systems and automation</li>
                <li>Estate Events, Weddings, and Hospitality digital channels</li>
                <li>Holiday Accommodation and retail digital presence</li>
                <li>AI and data tooling across the Estate</li>
              </BulletList>
            </Section>
          </div>

          <div>
            <StickyCard ref={formRef}>
              {submitted ? (
                <>
                  <CardTitle>Application Received</CardTitle>
                  <StatusMessage type="success">
                    Thank you — your application has been submitted successfully. We will be in touch with you soon.
                  </StatusMessage>
                  <SmallNote style={{ marginTop: '1.25rem', opacity: 0.75 }}>
                    In the meantime, feel free to explore the rest of belvoircastle.com.
                  </SmallNote>
                </>
              ) : (
                <>
                  <CardTitle>Apply for This Role</CardTitle>

                  <form onSubmit={handleSubmit} noValidate>
                    <FormGroup>
                      <Label>First Name <span className="req">*</span></Label>
                      <Input
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        placeholder="Jane"
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Last Name <span className="req">*</span></Label>
                      <Input
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        placeholder="Smith"
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Email Address <span className="req">*</span></Label>
                      <Input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="jane@example.com"
                        required
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Phone Number</Label>
                      <Input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+44 7700 900000"
                      />
                    </FormGroup>

                    <FormGroup>
                      <Label>Portfolio / GitHub / Website</Label>
                      <Input
                        type="url"
                        name="portfolio"
                        value={form.portfolio}
                        onChange={handleChange}
                        placeholder="https://github.com/..."
                      />
                    </FormGroup>

                    <Divider />

                    <FormGroup>
                      <Label>CV <span className="req">*</span></Label>
                      <FileUploadArea hasFile={!!cvFile}>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange(setCvFile)}
                        />
                        <FileUploadText selected={!!cvFile}>
                          {cvFile ? cvFile.name : 'Choose file — CV'}
                        </FileUploadText>
                        {!cvFile && (
                          <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>
                        )}
                      </FileUploadArea>
                    </FormGroup>

                    <FormGroup>
                      <Label>Cover Letter <span className="req">*</span></Label>
                      <FileUploadArea hasFile={!!coverFile}>
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx"
                          onChange={handleFileChange(setCoverFile)}
                        />
                        <FileUploadText selected={!!coverFile}>
                          {coverFile ? coverFile.name : 'Choose file — Cover Letter'}
                        </FileUploadText>
                        {!coverFile && (
                          <FileUploadHint>PDF, DOC or DOCX · Max 10MB</FileUploadHint>
                        )}
                      </FileUploadArea>
                    </FormGroup>

                    <Divider />

                    <FormGroup>
                      <Label>Cover Note <span className="req">*</span></Label>
                      <TextArea
                        name="coverNote"
                        value={form.coverNote}
                        onChange={handleChange}
                        placeholder="Tell us: what draws you to software and digital development; what you have built or contributed to technically; and what excites you about working at Belvoir Castle."
                        rows={5}
                        required
                      />
                      <SmallNote>Applications without a cover note will not be considered.</SmallNote>
                    </FormGroup>

                    {status && (
                      <StatusMessage type={status.type}>{status.message}</StatusMessage>
                    )}

                    <SubmitButton type="submit" disabled={submitting}>
                      {submitting ? 'Submitting…' : 'Submit Application'}
                    </SubmitButton>

                    <SmallNote style={{ marginTop: '0.85rem' }}>
                      Your information is kept confidential and used only for this recruitment process.
                    </SmallNote>
                  </form>
                </>
              )}
            </StickyCard>
          </div>
        </Grid>
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default SoftwareDigitalDevelopment;
